// withdrawCase.js — v242.106.0
//
// ★★★ ONE IMPLEMENTATION OF "CANCEL & WITHDRAW", BECAUSE THE SECOND CALLER IS
// WHAT MADE IT A UTILITY. Dr. Rami, 2026-08-21: "we also need to add the cancel
// and withdraw link to the case home page in the appointment management area",
// pointing at the Diagnose screen's button and saying "reuse this
// link/function".
//
// ★★ REUSE MEANT EXTRACT, NOT RETYPE. The body below was a 40-line inline
//    handler inside DiagnoseScreen, and TWENTY-TWO of those lines are a list of
//    fields that must be nulled. Copying that list into CaseHome would have
//    produced two withdrawal semantics that agree today and diverge the first
//    time anybody adds an appointment field — and the divergence would present
//    as "the case came back after I cancelled it", from one screen only. This
//    project has paid for that shape twice already (CASETRANSFORM-DUP-1, and the
//    wallet flag that lived in two components).
//
// ★ NULL, NOT undefined AND NOT {} — the store's smart-merge treats a truthy
//   value as "keep what the API last said", so an empty object RESURRECTS the
//   doctor on the next loadCases. That is why every field here is an explicit
//   null and why the caller must use updateAndSaveCase, not updateCase.

/**
 * The complete set of fields a withdrawal clears. Exported so a test can assert
 * the list rather than trusting that it was applied.
 */
export const WITHDRAWN_CASE_FIELDS = {
  status: 'ready_to_share',
  isShared: false,
  shareCode: null,
  share_code: null,
  sentToDoctor: null,
  sentToDoctorName: null,
  sentToDoctorAt: null,
  sentToDoctorData: null,
  doctor: null,
  doctor_id: null,
  doctorSource: null,
  doctor_source: null,
  appointmentRequest: null,
  appointmentStatus: null,
  scheduledAppointment: null,
  appointments: [],
  appointment: null,
  followUpAppointment: null,
  targetDoctorId: null,
  target_doctor_id: null,
}

// ★★★ TEST 3.17 (patient push 3a) — WHERE A WITHDRAWN CASE GOES. Both withdraw callers went to
//   /case/share-choice, and the fields above null doctorSource, so the fork's own auto-routing
//   (doctorSource 'orella' -> /case/share) could not fire: a patient who had sent the case to an
//   Orella physician landed on the fork, whose code card reads "Add your photos …" and goes to
//   the photo step. Alex, 2026-09-30: after withdrawing, straight to the doctor list.
//   Q82 (2026-09-30 late): a case first shared by CODE returns to the fork, and the fork does not
//   ask again for photos already on the case (hasCasePhotos / skipsDetect below).
//   The source is read from the case BEFORE the withdrawal clears it.
export const isOrellaSourced = (c) => !!c && (c.doctorSource === 'orella' || c.doctor_source === 'orella')

/** Where the patient lands after a withdrawal: the Orella doctor list, or the share hub (D468). */
export function withdrawDestination(caseBefore) {
  return isOrellaSourced(caseBefore) ? '/case/share' : '/case/share-hub'
}

/** Photos already on the case (Detect's uploads, or the API's photo list). */
export function hasCasePhotos(c) {
  if (!c) return false
  const n = (v) => (Array.isArray(v) ? v.length : 0)
  return n(c.detect?.photos) > 0 || n(c.photos) > 0
}

/** The fork's code path skips the photo step when Detect is done and the photos are there. */
export const skipsDetect = (c) => !!c && c.detectComplete === true && hasCasePhotos(c)

/**
 * Revoke the share code on the server, then clear the case locally.
 * Throws on failure so the caller can leave its modal open and stay honest.
 *
 * @param {string} caseId
 * @param {(updates: object) => Promise<any>} updateAndSaveCase - from useCaseStore
 * @param {object} [caseBefore] - the case as it was; an Orella-sourced case keeps its source
 */
export async function withdrawCaseFromDoctor(caseId, updateAndSaveCase, caseBefore) {
  if (!caseId) throw new Error('No case to withdraw.')
  const { casesAPI } = await import('../services/api')
  await casesAPI.revokeShareCode(caseId)
  // ★ An Orella-sourced case keeps doctorSource 'orella' (TEST 3.17). One literal payload, one
  //   write, so check-appointment-request-owner still reads every key it carries.
  const fields = { ...WITHDRAWN_CASE_FIELDS }
  if (isOrellaSourced(caseBefore)) { fields.doctorSource = 'orella'; fields.doctor_source = 'orella' }
  await updateAndSaveCase(fields)
}
